import React from "react";
import { PageHeader, CTASection } from "@/components/site/Bits";
import { Reveal, ClippedImage, ParallaxImage } from "@/lib/motion";

const SECTIONS = [
  {
    eyebrow: "The Experience",
    title: "Hospitality in Motion.",
    body: "ARIA was created around a simple idea: guests should never have to leave the moment to find a drink. Our hosts move naturally through the room, serving handcrafted cocktails with quiet precision, allowing every conversation, celebration and connection to continue uninterrupted.",
    img: "/api/media/gen-hero/raw",
    aspect: "aspect-[4/5]",
  },
  {
    eyebrow: "The Instrument",
    title: "More than a pour. A performance.",
    body: "Our vintage-inspired soda syphons are chosen not for nostalgia, but for what they create. A gentle flourish. A moment of surprise. A perfectly carbonated cocktail served with the quiet theatre that only thoughtful hospitality can deliver.",
    img: "/api/media/gen-cocktail/raw",
    aspect: "aspect-[4/5]",
  },
  {
    eyebrow: "The Arrival",
    title: "The welcome begins before the first toast.",
    body: "As guests arrive, ARIA is already among them. The first cocktails appear effortlessly, conversations begin naturally, and the atmosphere settles before anyone has searched for a bar. Hospitality arrives first. Everything else follows.",
    img: "/api/media/real-01/raw",
    aspect: "aspect-[4/5]",
  },
];

export default function Experience() {
  return (
    <div data-testid="experience-page">
      <PageHeader
        eyebrow="The Experience"
        title={["The art of", "the roaming pour."]}
        intro="ARIA is a service without a stage. It is composed to move with your evening — discreet, considered and entirely present."
        testid="experience-header"
      />

      <section className="aria-container pb-10 md:pb-20">
        <ParallaxImage
          src="/api/media/gen-hero/raw"
          alt="ARIA hosts serving cocktails in a candle-lit ballroom"
          className="aspect-[16/9] w-full"
          strength={50}
        />
      </section>

      {/* Narrative sections */}
      <section className="aria-container py-20 md:py-32" data-testid="experience-sections">
        <div className="flex flex-col gap-28 md:gap-48">
          {SECTIONS.map((s, i) => (
            <div
              key={s.eyebrow}
              className={`grid grid-cols-1 items-center gap-12 md:grid-cols-12 md:gap-20 ${
                i % 2 === 1 ? "md:[direction:rtl]" : ""
              }`}
            >
              <div className="md:col-span-6 md:[direction:ltr]">
                <ClippedImage src={s.img} alt={s.title} aspect={s.aspect} />
              </div>
              <div className="md:col-span-6 md:[direction:ltr]">
                <div className="eyebrow mb-6">{s.eyebrow}</div>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight text-aria-ink">{s.title}</h2>
                <Reveal delay={0.15}>
                  <p className="mt-8 max-w-md text-xl leading-relaxed text-aria-cocoa">{s.body}</p>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* The Craft — the syphon difference */}
      <section className="bg-aria-sand py-28 md:py-40" data-testid="experience-craft">
        <div className="aria-container">
          <div className="eyebrow mb-8">The Craft</div>
          <p className="max-w-4xl font-display text-3xl md:text-4xl lg:text-5xl leading-[1.15] text-aria-ink text-balance">
            At ARIA, cocktails aren't just mixed — they're awakened. What makes us unique is the
            classic soda syphon, a vintage-inspired instrument that transforms an ordinary cocktail
            into an effervescent experience.
          </p>

          <div className="mt-16 grid grid-cols-1 gap-12 md:grid-cols-3 md:gap-12">
            {[
              { n: "01", t: "Precision Meets Theatre", b: "Each cocktail is charged with bubbles at the very moment it is served, in front of your guests. The soft hiss of the syphon, the rising fizz and the sparkle in the glass become a quiet performance." },
              { n: "02", t: "Flavours That Sing", b: "Unlike pre-batched or bottled spritzers, syphon carbonation locks in freshness. Citrus tastes brighter, herbs feel more alive and every sip is lighter on the palate." },
              { n: "03", t: "An Unforgettable Impression", b: "Guests do not simply receive a drink — they keep a memory. The visual flair and singular texture of syphon service set an evening apart." },
            ].map((f, i) => (
              <Reveal key={f.n} delay={i * 0.1}>
                <div className="border-t border-aria-travertine pt-8">
                  <span className="font-accent italic text-4xl text-aria-brass/60">{f.n}</span>
                  <h3 className="mt-6 font-display text-2xl md:text-3xl leading-tight text-aria-ink">{f.t}</h3>
                  <p className="mt-4 text-lg leading-relaxed text-aria-cocoa">{f.b}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <p className="mt-20 max-w-3xl font-display text-2xl md:text-3xl italic leading-snug text-aria-cocoa">
              With ARIA, we do not simply bring cocktails. We bring conversation, atmosphere and a
              touch of quiet magic that lingers long after the glass is empty.
            </p>
          </Reveal>
        </div>
      </section>

      {/* The Movement */}
      <section className="bg-aria-ink text-aria-ivory" data-testid="experience-movement">
        <div className="aria-container grid grid-cols-1 items-center gap-14 py-28 md:grid-cols-12 md:py-40">
          <div className="md:col-span-6">
            <div className="eyebrow mb-8">The Movement</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.04] text-aria-ivory">
              Designed to move with every celebration.
            </h2>
          </div>
          <div className="md:col-span-6">
            <Reveal delay={0.1}>
              <p className="font-display text-2xl md:text-3xl leading-snug text-aria-ivory">
                A single stationary bar serves one location. ARIA serves the entire event.
              </p>
              <p className="mt-8 max-w-lg font-light text-lg leading-relaxed text-aria-ivory/60">
                Whether guests are gathered beneath a vineyard pergola, wandering through a museum gallery, networking across a rooftop or celebrating beneath a wedding tent, the experience follows naturally — never interrupting the moment, only enriching it.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <CTASection eyebrow="Begin" title={["Compose your", "own evening."]} />
    </div>
  );
}
